import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { EventType, Roster } from '../../../../../../../models/event-type';
import { LocationService } from '../../../../../../../services/location/location.service';
import { AccountService } from '../../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import { CapitanLocation } from '../../../../../../../models/location';
import { getAllowedLocationsForEventType } from '../../../../../../../utils-models';


@Component({
  selector: 'app-event-type-roster-edit',
  templateUrl: './event-type-roster-edit.component.html',
  styleUrls: ['./event-type-roster-edit.component.css']
})
export class EventTypeRosterEditComponent implements OnInit {
  public eventType: EventType = null;

  public isNewRoster: boolean;
  public roster: Roster = {
    id: null,
    url: '',
    event_type_id: null,
    event_type_name: '',
    location_id: null,
    location_name: '',
    time_zone: '',
    name: '',
    capacity: null,
    order_number: 0,
    schedules: [],
    current_member_count: 0,
  };
  public previousCapacity: number;

  public loadingEventType = true;
  public loadingRoster = false;
  public loadingSave = false;

  public notFound = false;
  public errorMessage = '';
  public errors: any = {};
  public serverError: number = null;

  private locations: CapitanLocation[] = null;

  constructor(
    public locationService: LocationService,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    const eventTypeIdInt = parseInt(routeParams.eventTypeId, 10);
    if (isNaN(eventTypeIdInt)) {
      this.notFound = true;
      return;
    }

    this.loadEventType(eventTypeIdInt);
    this.loadLocations();

    this.isNewRoster = !routeParams.hasOwnProperty('rosterId');

    // Get the item to edit, if an ID was provided.
    if (!this.isNewRoster) {
      const rosterIdInt = parseInt(routeParams.rosterId, 10);
      if (isNaN(rosterIdInt)) {
        this.notFound = true;
        return;
      }
      this.loadRoster(rosterIdInt);
    } else {
      this.roster.event_type_id = eventTypeIdInt;
      this.roster.location_id = this.accountService.getMyAccount().default_gym_id;
    }
  }

  public isLoading() {
    return (
      this.loadingEventType ||
      this.loadingRoster ||
      this.loadingSave
    );
  }

  public save() {
    this.loadingSave = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<Roster>;
    if (this.isNewRoster) {
      savePromise = this.eventTypeService.addRoster(this.roster);
    } else {
      savePromise = this.eventTypeService.updateRoster(this.roster);
    }

    savePromise
      .then(savedRoster => {
        this.router.navigate([
          '/events/setup/edit/' + this.eventType.id.toString() + '/rosters'
        ]);
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          if (this.isNewRoster) {
            this.errors = 'You do not have permission to add rosters. The "Organization Admin" permission is required.';
          } else {
            this.errors = 'You do not have permission to modify this roster. The "Organization Admin" permission is required.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSave = false; });
  }

  public getAllowedLocations() {
    return getAllowedLocationsForEventType(this.eventType, this.locations);
  }

  private loadEventType(eventTypeId: number) {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  private loadRoster(rosterId: number) {
    this.loadingRoster = true;

    this.eventTypeService
      .getRoster(rosterId)
      .then(roster => {
        this.roster = roster;
        this.previousCapacity = this.roster.capacity;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingRoster = false; });
  }

  private loadLocations() {
    this.locations = this.locationService.getLocations();
    if (this.locations === null) {
      this.locationService
        .loadLocations()
        .then(() => {
          this.locations = this.locationService.getLocations();
        });
    }
  }
}
